import set from "@rill/set";
import { round } from "lodash";
import moment from "moment";
import ActivityDrawer from "../../../../parts/activity-drawer/ActivityDrawer";
import JWPlayerVideoModal from "../../../../parts/modals/jwplayer-video";
import { formattedTarget } from "../../../../utils/actuals";
import { frequencyLimit } from "../../../../utils/questionnaire";
import {
  buildVideoModalUrl,
  findVideoNeighbors,
} from "../../../../utils/video-modal-url";
import CheckinSummary from "./CheckinSummary";
import { isAdminOrOwner } from "../../../../utils/organization-permissions-helpers";

const CheckInSummaryController = (app) => {
  app.get(
    "/check-in-summary",
    set("heading", "Check-in Summary"),
    async ({ req, res, locals, load }) => {
      const { query } = req;
      const {
        athlete,
        checkInDate,
        checkInFrequency,
        athleteSwitch,
        organizationCoach,
      } = query;
      const modal = query.modal || {};

      const isAllowToViewAllOrganizationsAthletes = isAdminOrOwner(
        locals.organizationRoleName
      );

      if (
        locals.activeUser.organizationId &&
        isAllowToViewAllOrganizationsAthletes &&
        !locals.allAthletesFromOrganization
      ) {
        await load("allAthletesFromOrganization");
      }

      // Basically the start of the year 2023 as we don't need information before then
      const dailyStart = moment("2023-06-01")
        .startOf("year")
        .format("YYYY-MM-DD");

      await load("dailyStats", {
        proxyAthlete: athlete,
        start: dailyStart,
      });

      let selectedAnswer = null;

      let effectiveCheckInDate = checkInDate;
      let effectiveCheckInFrequency = checkInFrequency;

      if (!effectiveCheckInDate && !athleteSwitch) {
        const latest = locals.dailyStats
          .filter((s) => !!s.questionnaire)
          .reduce((a, b) => (!a || b.utcDate > a.utcDate ? b : a), null);

        if (latest) {
          effectiveCheckInDate = moment(
            String(latest.utcDate),
            "YYYYMMDD"
          ).format("YYYY-MM-DD");
          effectiveCheckInFrequency =
            latest.questionnaire.questionnaireFrequency;
        }
      }

      if (effectiveCheckInDate && !athleteSwitch) {
        // Minimum of 7 days, this is for the new loom feature that doesn't have access to the frequency
        const dayLimit = frequencyLimit[effectiveCheckInFrequency] || 7;

        const end = moment(effectiveCheckInDate).format("YYYY-MM-DD");
        const start = moment(end)
          .subtract(dayLimit, "days")
          .format("YYYY-MM-DD");

        selectedAnswer = locals.dailyStats.find(
          (stat) =>
            stat.utcDate ===
              Number(moment(effectiveCheckInDate).format("YYYYMMDD")) &&
            !!stat.questionnaire
        );

        if (selectedAnswer) {
          await Promise.all([
            load("nutritionPlans", {
              proxyAthlete: athlete,
            }),
            load("activeExercises", {
              proxyAthlete: athlete,
              reqSource: "calendar",
              start,
              end,
            }),
            load("dayPersonalRecords", {
              start: moment(start).format("YYYYMMDD"),
              end: moment(end).isAfter(moment(), "day")
                ? moment().endOf("day").format("YYYYMMDD")
                : moment(end).startOf("day").format("YYYYMMDD"),
              proxyAthlete: athlete,
              isCheckIn: true,
            }),
            load("videoListMetrics", {
              page: 1,
              limit: 50, // 50 videos, might change in the future
              // the date column header toggles this. It has to be the api that
              // sorts: the modal's prev/next is built from this list, so a
              // client-side re-sort of the table would leave the two disagreeing
              sort: Number(query.videoSort) === 1 ? 1 : -1,
              proxyAthlete: athlete,
              // `start` is inclusive-minus-one (dayLimit vs the displayed
              // dayLimit-1 window), so bump the video `from` by a day to match
              // the Jun 30–Jul 6 range shown in the UI and exclude the prior day.
              from: moment.utc(start, "YYYY-MM-DD").add(1, "day").toDate(),
              to: moment.utc(end, "YYYY-MM-DD").toDate(),
            }),
            load("videoRecord", effectiveCheckInDate, athlete, "check-in"),
            load("workoutNote", effectiveCheckInDate, athlete, "check-in"),
            load(
              "workoutCoachNote",
              effectiveCheckInDate,
              athlete,
              organizationCoach || locals.activeUser._id,
              "check-in"
            ),
            load("femaleCycleLogs", { proxyAthlete: athlete }),
            load("femaleCycleSettings", { proxyAthlete: athlete }),
          ]);
        }
      }

      if (query.threadId) {
        await load("comments", {
          threadId: query.threadId,
        });
      } else {
        locals.comments = [];
      }

      // Build prev/next neighbors for watch-video-list modal
      let prevHref;
      let nextHref;
      if (modal.type === "watch-video-list") {
        const athleteId = locals.athlete
          ? locals.athlete._id
          : locals.activeUser._id;
        const sequence = (locals.videoListMetrics?.exercises || []).map(
          (ex) => {
            const exSetIndex = Number.parseInt(ex.setIndex, 10);
            const exVideoIndex = Number.parseInt(ex.videoSet, 10);
            const seqOutcomeObj = ex.outcomes?.outcome || {};
            const seqExType = ex.type ?? ex.exercise?.type ?? "";
            const seqIsPowerlifting =
              seqExType === "primary" || seqExType === "secondary";
            const { units, intensityMeasure } = locals.activeUser;
            const seqPrecision = units === "metric" ? 2 : 1;
            const seqUserMetrics = units === "imperial" ? "lb" : "kg";
            const seqRawLoad = seqOutcomeObj.load;
            const seqLoad = seqRawLoad
              ? `${round(locals.fromMetric(seqRawLoad, units), seqPrecision)} ${seqUserMetrics}`
              : 0;
            const seqTarget =
              seqRawLoad || seqOutcomeObj.reps || seqOutcomeObj.rpe
                ? formattedTarget(
                    seqOutcomeObj,
                    seqIsPowerlifting,
                    ex.set?.percentRepMax || undefined,
                    units,
                    seqLoad,
                    intensityMeasure,
                    intensityMeasure !== "rpe",
                    true
                  ) || undefined
                : undefined;
            const seqSetNumber = ex.setNumber;
            return {
              key: `${ex.exerciseId}:${exSetIndex}:${exVideoIndex}`,
              href: buildVideoModalUrl({
                modalType: "watch-video-list",
                video: {
                  videoId: ex.videoId,
                  videoOriginal: ex.videoOriginal,
                  reviewedByTheCoach: ex.reviewedByTheCoach,
                  uploaderId: ex.uploaderId || athleteId,
                  thread: ex.thread,
                },
                exerciseToEdit: ex.exerciseId,
                setIndex: exSetIndex,
                videoIndex: exVideoIndex,
                utcDate: ex.utcDate,
                primary: ex.primary ?? ex.exercise?.primary,
                secondary: ex.secondary ?? ex.exercise?.secondary,
                type: ex.type ?? ex.exercise?.type,
                movement: ex.movement ?? ex.exercise?.movement,
                setNumber: seqSetNumber,
                setTarget: seqTarget,
                setComment: ex.comments || undefined,
              }),
            };
          }
        );
        const currentKey = `${query.exerciseToEdit}:${Number.parseInt(query.setIndex, 10)}:${Number.parseInt(query.videoIndex, 10)}`;
        ({ prevHref, nextHref } = findVideoNeighbors({ sequence, currentKey }));
      }

      locals.modal = {
        "watch-video-list": (
          <JWPlayerVideoModal
            athleteId={query.mediaAthleteId || query.athlete}
            exerciseToEdit={query.exerciseToEdit}
            locals={locals}
            mediaId={query.mediaId}
            movement={query.movement}
            originalId={query.originalId}
            primary={query.primary}
            reviewedByTheCoach={query.reviewedByTheCoach}
            secondary={query.secondary}
            setIndex={query.setIndex}
            threadId={query.threadId}
            type={query.type}
            utcDate={query.utcDate}
            videoIndex={query.videoIndex}
            prevHref={prevHref}
            nextHref={nextHref}
            setNumber={query.setNumber}
            setTarget={query.setTarget}
            setComment={query.setComment}
          />
        ),
        "activity-feed": <ActivityDrawer />,
      }[modal.type];

      res.body = (
        <CheckinSummary locals={locals} selectedAnswer={selectedAnswer} />
      );
    }
  );
};

export default CheckInSummaryController;
